'use client';

import React from 'react';
import CardFlip from '@/components/ui/CardFlip';
import { motion } from 'framer-motion';
import { Feather } from 'lucide-react';

const SkillsSection = () => {
  const skillsData: Array<{
    title: string;
    subtitle: string;
    description: string;
    features: string[];
    // linkUrl: string;
    // linkText: string;
    theme: 'primary' | 'emerald' | 'orange' | 'secondary' | 'rose';
  }> = [
    {
      title: 'Frontend Development',
      subtitle: 'Modern Web Technologies',
      description:
        'Building responsive, interactive user interfaces with React, TypeScript, and modern CSS frameworks.',
      features: [
        'React & Next.js',
        'TypeScript',
        'Tailwind CSS',
        'Framer Motion',
      ],
      // linkUrl: '/skills/frontend',
      // linkText: 'Learn More',
      theme: 'primary',
    },
    {
      title: 'Backend Architecture',
      subtitle: 'Scalable Solutions',
      description:
        'Designing robust server-side systems with Node.js, databases, and cloud infrastructure.',
      features: [
        'Node.js & Express',
        'Database Design',
        'API Development',
        'Cloud Deployment',
      ],
      // linkUrl: '/skills/backend',
      // linkText: 'Explore',
      theme: 'emerald',
    },
    {
      title: 'AI & Machine Learning',
      subtitle: 'Intelligent Systems',
      description:
        'Implementing AI solutions and machine learning models for real-world applications.',
      features: [
        'Python & TensorFlow',
        'NLP & Computer Vision',
        'Model Training',
        'AI Integration',
      ],
      // linkUrl: '/skills/ai',
      // linkText: 'Discover',
      theme: 'orange',
    },
    {
      title: 'DevOps & Deployment',
      subtitle: 'Production Ready',
      description:
        'Streamlining development workflows and ensuring reliable production deployments.',
      features: [
        'CI/CD Pipelines',
        'Docker & Kubernetes',
        'Monitoring',
        'Security',
      ],
      // linkUrl: '/skills/devops',
      // linkText: 'See More',
      theme: 'secondary',
    },
    {
      title: 'Solution Architecture',
      subtitle: 'Enterprise Solutions',
      description:
        'Designing scalable, secure, and maintainable enterprise solutions that align with business objectives.',
      features: [
        'System Design',
        'Cloud Architecture',
        'Security Planning',
        'Technical Leadership',
      ],
      // linkUrl: '/skills/architecture',
      // linkText: 'View Solutions',
      theme: 'rose',
    },
  ];

  return (
    <section className="from-card/0 via-background/10 to-background relative overflow-hidden bg-gradient-to-br">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="bg-primary/5 absolute -right-40 -bottom-40 h-80 w-80 rounded-full blur-3xl" />
        <div className="bg-secondary/0 absolute -top-40 -left-40 h-80 w-80 rounded-full blur-3xl" />
        <div className="bg-accent/2 absolute top-1/2 left-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl" />
      </div>

      <div className="relative container mx-auto px-4 py-40 md:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16 text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-primary/10 border-primary/20 mb-6 inline-flex items-center gap-3 rounded-full border px-6 py-3"
          >
            <Feather className="h-5 w-5 text-blue-500" />
            <span className="text-sm font-medium text-blue-400">Skills</span>
          </motion.div>
          <div className="from-foreground via-primary to-foreground mb-2 bg-gradient-to-r bg-clip-text text-4xl leading-tight font-bold text-transparent md:text-5xl lg:text-6xl xl:text-7xl">
            <span className="text-foreground">Skills &</span> Expertise
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="text-foreground mx-auto max-w-2xl font-serif text-lg leading-relaxed md:max-w-4xl md:text-xl lg:text-2xl"
          >
            A comprehensive overview of my technical skills and professional
            expertise.
          </motion.p>
        </motion.div>

        {/* Bento Grid */}
        <div className="mx-auto w-full max-w-7xl">
          <section className="relative overflow-hidden">
            <div className="container p-6 lg:px-8">
              <ul className="grid-rows-auto grid grid-cols-1 gap-4 md:grid-cols-12 lg:gap-4 xl:max-h-[42rem]">
                {/* Frontend Development - Large featured card */}
                <motion.li
                  initial={{ opacity: 0, y: 30, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  className="border-primary/20 bg-primary/5 min-h-[20rem] list-none rounded-3xl border p-2 md:[grid-area:1/1/2/7] xl:[grid-area:1/1/2/5]"
                >
                  <CardFlip {...skillsData[0]} />
                </motion.li>

                {/* Backend Architecture - Medium card */}
                <motion.li
                  initial={{ opacity: 0, y: 30, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="border-primary/20 bg-primary/5 min-h-[20rem] list-none rounded-3xl border p-2 md:[grid-area:1/7/2/13] xl:[grid-area:2/1/3/5]"
                >
                  <CardFlip {...skillsData[1]} />
                </motion.li>

                {/* AI & Machine Learning - Medium card */}
                <motion.li
                  initial={{ opacity: 0, y: 30, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="border-primary/20 bg-primary/5 min-h-[20rem] list-none rounded-3xl border p-2 md:[grid-area:2/1/3/7] xl:[grid-area:1/5/3/8]"
                >
                  <CardFlip {...skillsData[2]} />
                </motion.li>

                {/* DevOps & Deployment - Medium card */}
                <motion.li
                  initial={{ opacity: 0, y: 30, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="border-primary/20 bg-primary/5 min-h-[20rem] list-none rounded-3xl border p-2 md:[grid-area:2/7/3/13] xl:[grid-area:1/8/2/13]"
                >
                  <CardFlip {...skillsData[3]} />
                </motion.li>

                {/* Solution Architecture - Medium card */}
                <motion.li
                  initial={{ opacity: 0, y: 30, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="border-primary/20 bg-primary/5 min-h-[20rem] list-none rounded-3xl border p-2 md:[grid-area:3/1/4/13] xl:[grid-area:2/8/3/13]"
                >
                  <CardFlip {...skillsData[4]} />
                </motion.li>
              </ul>
            </div>
          </section>
        </div>
      </div>
    </section>
  );
};

export default SkillsSection;
